import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiPost } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { bookingKeys, schedulerKeys } from '@/lib/queryKeys';

export interface WeatherBatchResult {
  applied: number;
  skipped: number;
  results: { appointmentId: string; ok: boolean; error?: string }[];
}

export interface WeatherHoldPreviewResult {
  skip: number;
  makeup: number;
  revenueAtRisk: number;
  currency: string;
  perVisit: Array<{ id: string; policy: 'SKIP' | 'MAKE_UP' }>;
}

export interface WeatherPlacement {
  appointmentId: string;
  technicianId: string;
  startAt: string;
  endAt: string;
  targetDate: string;
  originalDate: string;
}

export interface WeatherReabsorbPlacement extends WeatherPlacement {
  insertionCostMinutes: number;
  score: number;
  sameTechnicianPreferred: boolean;
  technicianName: string;
}

export interface WeatherFlexPreviewResult {
  placements: WeatherPlacement[];
  skipped: { appointmentId: string; ok: boolean; error?: string }[];
}

export interface WeatherPreviewResult {
  placements: WeatherReabsorbPlacement[];
  skipped: { appointmentId: string; ok: boolean; error?: string }[];
}

export interface WeatherReabsorbMove {
  appointmentId: string;
  technicianId: string;
  startAt: string;
}

export interface WeatherPreviewRequest {
  appointmentIds: string[];
  makeupOverrideIds?: string[];
  skipOverrideIds?: string[];
}

function useWeatherMutation(endpoint: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    WeatherBatchResult,
    {
      appointmentIds: string[];
      release?: boolean;
      makeupOverrideIds?: string[];
      skipOverrideIds?: string[];
    }
  >({
    mutationFn: (body) =>
      apiPost<WeatherBatchResult, typeof body>(operationsClient, endpoint, body),
    showErrorToast: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
    },
  });
}

export function useWeatherHoldAction() {
  return useWeatherMutation(schedulerEndpoints.dispatchWeatherHold());
}

export function useWeatherHoldPreviewAction() {
  return useApiMutation<WeatherHoldPreviewResult, WeatherPreviewRequest>({
    mutationFn: (body) =>
      apiPost<WeatherHoldPreviewResult, typeof body>(
        operationsClient,
        schedulerEndpoints.dispatchWeatherHoldPreview(),
        body,
      ),
    showErrorToast: false,
  });
}

export function useWeatherSkipAction() {
  return useWeatherMutation(schedulerEndpoints.dispatchWeatherSkip());
}

export function useWeatherFlexApplyAction() {
  return useWeatherMutation(schedulerEndpoints.dispatchWeatherFlexApply());
}

export function useWeatherFlexPreviewAction() {
  return useApiMutation<WeatherFlexPreviewResult, WeatherPreviewRequest>({
    mutationFn: (body) =>
      apiPost<WeatherFlexPreviewResult, typeof body>(
        operationsClient,
        schedulerEndpoints.dispatchWeatherFlexPreview(),
        body,
      ),
    showErrorToast: false,
  });
}

export function useWeatherReabsorbPreviewAction() {
  return useApiMutation<WeatherPreviewResult, WeatherPreviewRequest>({
    mutationFn: (body) =>
      apiPost<WeatherPreviewResult, typeof body>(
        operationsClient,
        schedulerEndpoints.dispatchWeatherReabsorbPreview(),
        body,
      ),
    showErrorToast: false,
  });
}

export function useWeatherReabsorbApplyAction() {
  const queryClient = useQueryClient();
  return useApiMutation<WeatherBatchResult, { moves: WeatherReabsorbMove[] }>({
    mutationFn: (body) =>
      apiPost<WeatherBatchResult, typeof body>(
        operationsClient,
        schedulerEndpoints.dispatchWeatherReabsorbApply(),
        body,
      ),
    showErrorToast: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
    },
  });
}
